import { X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { parseApiError } from '../../helpers/billingApi';
import {
  confirmAndSaveCard,
  defaultPaymentMethod,
  describeCard,
  describeExpiry,
  fetchPaymentMethods,
  setDefaultPaymentMethod,
} from '../../helpers/paymentMethodsApi';
import {
  DEFAULT_INTERVAL,
  DEFAULT_PLAN_KEY,
  findChoice,
  normalizeInterval,
  usePlanChoices,
  visibleChoices,
} from '../../helpers/plans';
import { getStripe } from '../../helpers/stripeClient';
import CardField from './CardField';
import { formatCurrency } from './adminUtils';

const NEW_CARD = 'new';

const intervalWord = (interval) => {
  const value = normalizeInterval(interval);
  if (value === 'yearly' || value === 'year') return 'year';
  if (value === 'monthly' || value === 'month') return 'month';
  return value || 'period';
};

// Plan + payment in one step. The API charges the customer's Stripe default
// payment method immediately and rejects the subscribe with
// PAYMENT_METHOD_REQUIRED when there isn't one, so this collects (or confirms)
// the card *before* calling onConfirm rather than letting the user find out
// after they have already picked a plan.
function ConfirmSubscribe({ defaultPlanKey, defaultInterval, onConfirm, onClose, busy }) {
  const stripe = useStripe();
  const elements = useElements();

  const { choices, loaded, loading, error } = usePlanChoices(true);
  const [selectedId, setSelectedId] = useState(null);

  const [methods, setMethods] = useState([]);
  const [methodsLoaded, setMethodsLoaded] = useState(false);
  const [methodId, setMethodId] = useState(NEW_CARD);
  const [cardError, setCardError] = useState('');
  const [payError, setPayError] = useState('');
  const [preparing, setPreparing] = useState(false);

  // Preselect the caller's plan when the catalog still offers it — including a
  // hidden one, so resubscribing keeps the customer on the plan they were
  // actually on. Otherwise fall back to the first plan we do promote, so a
  // retired plan name can't dead-end the modal.
  useEffect(() => {
    if (!choices.length) return;
    const preferred = findChoice(choices, defaultPlanKey, defaultInterval);
    const fallback = choices.find((choice) => !choice.hidden) || choices[0];
    setSelectedId((preferred || fallback).id);
  }, [choices, defaultPlanKey, defaultInterval]);

  const loadMethods = useCallback(async () => {
    try {
      const saved = await fetchPaymentMethods();
      setMethods(saved);
      const fallback = defaultPaymentMethod(saved);
      setMethodId(fallback ? fallback.id : NEW_CARD);
    } catch (requestError) {
      setPayError(parseApiError(requestError, 'Could not load your saved cards.'));
    } finally {
      setMethodsLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadMethods();
  }, [loadMethods]);

  const options = useMemo(() => visibleChoices(choices, selectedId), [choices, selectedId]);
  const selected = options.find((choice) => choice.id === selectedId) || null;
  const amountLabel = selected ? formatCurrency(selected.amount, selected.currency) : null;
  const period = selected ? intervalWord(selected.interval) : null;
  const noPlans = loaded && !error && !options.length;

  const usingNewCard = methodId === NEW_CARD;
  const chosenMethod = methods.find((method) => method.id === methodId) || null;
  const working = busy || preparing;

  // Make sure a chargeable default card exists, then hand the plan to the
  // parent. Card problems are reported in this form, so the parent only ever
  // sees a subscribe it can actually complete.
  const handleConfirm = async () => {
    if (!selected || working) return;
    setCardError('');
    setPayError('');

    try {
      if (usingNewCard) {
        setPreparing(true);
        const { cardError: rejected } = await confirmAndSaveCard({ stripe, elements });
        if (rejected) {
          setCardError(rejected);
          setPreparing(false);
          return;
        }
        await loadMethods();
      } else if (chosenMethod && !chosenMethod.is_default) {
        // Billing charges the Stripe default, so promote the picked card first.
        setPreparing(true);
        await setDefaultPaymentMethod(chosenMethod.id);
      }
      setPreparing(false);
    } catch (requestError) {
      setPayError(parseApiError(requestError, 'Could not save your payment method.'));
      setPreparing(false);
      return;
    }

    await onConfirm(selected);
  };

  return (
    <>
      <header className="modal-head">
        <div className="modal-head-copy">
          <div className="modal-kicker">
            <span>CONFIRM SUBSCRIPTION</span>
            {selected?.planName || 'Choose a plan'}
          </div>
        </div>
        <button className="icon-button" type="button" onClick={onClose} aria-label="Close confirmation">
          <X size={16} />
        </button>
      </header>

      <div className="modal-body">
        <h2 id="confirm-sub-title">Confirm your subscription</h2>
        {loading ? <p>Loading plans...</p> : null}
        {error ? <div className="error-banner">{error}</div> : null}
        {noPlans ? (
          <div className="error-banner">No plans are available right now. Contact support.</div>
        ) : null}

        {options.length > 1 ? (
          <fieldset className="plan-options">
            <legend>Choose a plan</legend>
            {options.map((choice) => (
              <label
                className={`plan-option${choice.id === selectedId ? ' is-selected' : ''}`}
                key={choice.id}
              >
                <input
                  type="radio"
                  name="plan-choice"
                  value={choice.id}
                  checked={choice.id === selectedId}
                  onChange={() => setSelectedId(choice.id)}
                  disabled={working}
                />
                <span className="plan-option-name">{choice.planName}</span>
                <span className="plan-option-price">
                  {formatCurrency(choice.amount, choice.currency)} / {intervalWord(choice.interval)}
                </span>
              </label>
            ))}
          </fieldset>
        ) : null}

        {selected ? (
          <>
            <div className="charge-summary">
              <span>You will be charged now</span>
              <strong>{amountLabel}</strong>
              <small>
                {selected.planName} - then {amountLabel} every {period}
              </small>
            </div>

            <fieldset className="plan-options">
              <legend>Payment method</legend>
              {payError ? <div className="error-banner">{payError}</div> : null}
              {!methodsLoaded ? <p>Loading payment methods...</p> : null}

              {methods.map((method) => (
                <label
                  className={`plan-option${method.id === methodId ? ' is-selected' : ''}`}
                  key={method.id}
                >
                  <input
                    type="radio"
                    name="payment-method"
                    value={method.id}
                    checked={method.id === methodId}
                    onChange={() => setMethodId(method.id)}
                    disabled={working}
                  />
                  <span className="plan-option-name">{describeCard(method)}</span>
                  <span className="plan-option-price">{describeExpiry(method)}</span>
                </label>
              ))}

              {methodsLoaded && methods.length ? (
                <label className={`plan-option${usingNewCard ? ' is-selected' : ''}`}>
                  <input
                    type="radio"
                    name="payment-method"
                    value={NEW_CARD}
                    checked={usingNewCard}
                    onChange={() => setMethodId(NEW_CARD)}
                    disabled={working}
                  />
                  <span className="plan-option-name">Use a new card</span>
                </label>
              ) : null}

              {methodsLoaded && usingNewCard ? (
                <CardField
                  label={methods.length ? 'New card details' : 'Card details'}
                  error={cardError}
                  onChange={() => setCardError('')}
                />
              ) : null}
            </fieldset>

            <p>Your card is charged immediately. Cancel anytime; your site stays online for 30 days after canceling.</p>
          </>
        ) : null}
      </div>

      <footer className="modal-foot">
        <span>BILLED IMMEDIATELY - NO TRIAL</span>
        <div>
          <button className="btn btn-ghost" type="button" onClick={onClose} disabled={working}>
            No, cancel
          </button>
          <button
            className="btn btn-primary"
            type="button"
            onClick={handleConfirm}
            disabled={
              working ||
              !loaded ||
              !!error ||
              !selected ||
              !methodsLoaded ||
              (usingNewCard && !stripe)
            }
          >
            {working ? 'Processing...' : amountLabel ? `Yes, pay ${amountLabel}` : 'Yes, subscribe'}
          </button>
        </div>
      </footer>
    </>
  );
}

// Shell: owns the dialog chrome and the Elements provider so the body can use
// the Stripe hooks. Unmounted while closed, so every open starts clean.
export default function ConfirmSubscribeModal({
  open,
  defaultPlanKey = DEFAULT_PLAN_KEY,
  defaultInterval = DEFAULT_INTERVAL,
  onConfirm,
  onClose,
  busy,
}) {
  if (!open) return null;
  const stripePromise = getStripe();

  return (
    <div className="modal-backdrop" role="presentation">
      <section className="create-modal" role="dialog" aria-modal="true" aria-labelledby="confirm-sub-title">
        {stripePromise ? (
          <Elements stripe={stripePromise}>
            <ConfirmSubscribe
              defaultPlanKey={defaultPlanKey}
              defaultInterval={defaultInterval}
              onConfirm={onConfirm}
              onClose={onClose}
              busy={busy}
            />
          </Elements>
        ) : (
          <>
            <header className="modal-head">
              <div className="modal-head-copy">
                <div className="modal-kicker">
                  <span>CONFIRM SUBSCRIPTION</span>
                  Unavailable
                </div>
              </div>
              <button className="icon-button" type="button" onClick={onClose} aria-label="Close confirmation">
                <X size={16} />
              </button>
            </header>
            <div className="modal-body">
              <h2 id="confirm-sub-title">Confirm your subscription</h2>
              <div className="error-banner">
                Card payments are not configured. Set REACT_APP_STRIPE_PUBLISHABLE_KEY and reload.
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
